import React from 'react';
import PropTypes from 'prop-types';

import Button from '../../ui/Button';
import Popover from '../../ui/Popover';

import TaraHeader from './TaraHeader';
import TaraPopoverFooter from './TaraPopoverFooter';
import TaraQuestionPrice from './TaraQuestionPrice';
import TaraQuestionQuantity from './TaraQuestionQuantity';
import TaraQuestionPersona from './TaraQuestionPersona';

const noop = () => {};

const DefaultMessage = () => (
  <div>
    <div>Hi there!</div>
    <div>
      <span>I&apos;m </span>
      <strong>TARA</strong>
      <span>, and I'm your AI powered assistant for locating the best tickets to any event!</span>
    </div>
  </div>
);

const IntroductionTaraMessage = ({ eventName }) => (
  <div>
    <div>Hi there!</div>
    <div>
      <span>I&apos;m </span>
      <strong>TARA</strong>
      <span>, and I'm your AI powered assistant for locating the best tickets to </span>
      <strong>{eventName}</strong>
      <span>!</span>
    </div>
  </div>
);

const TaraMessage = () => (
  <div>
    <div>Didn&apos;t find what you&apos;re looking for?</div>
    <div>Try different options or browse all tickets!</div>
  </div>
);

const CuratedTaraMessage = ({ eventName }) => (
  <div>
    <div>Great!</div>
    <div>Now lets find the best tickets to <strong>{eventName}</strong></div>
  </div>
);

const styleButton = {
  height: 48,
  width: '100%',
};

class TaraPopover extends React.PureComponent {
  static propTypes = {
    onClickBrowseAllTickets: PropTypes.func,
    onClickFindMyTickets: PropTypes.func,

    hasButton: PropTypes.bool,
    hasMadeIntroduction: PropTypes.bool,
    isAnimated: PropTypes.bool,
    isOpen: PropTypes.bool,
  };

  static defaultProps = {
    onClickBrowseAllTickets: noop,
    onClickFindMyTickets: noop,

    hasButton: false,
    hasMadeIntroduction: false,
    isAnimated: false,
    isOpen: undefined,
  };

  state = {
    isReady: false,
    priceLow: this.props.priceLow ? parseInt(this.props.priceLow, 10) : 1,
    priceHigh: this.props.priceHigh ? parseInt(this.props.priceHigh, 10) : 5,
    persona: this.props.persona || null,
    quantity: this.props.quantity || null,
  };

  get formState() {
    const { isReady, ...formState } = this.state;

    return formState;
  }

  componentDidMount() {
    this.hasMounted = true;
    setTimeout(() => {
      this.setIsReady(true);
    }, 1600);
  }

  componentWillReceiveProps(nextProps) {
    const {
      currentEvent,
      hasCuratedTickets,
      hasMadeIntroduction,
    } = nextProps;

    const isChangingCurrentEvent = currentEvent !== this.props.currentEvent;
    const isChangingHasCuratedTickets = hasCuratedTickets !== this.props.hasCuratedTickets;
    const isChangingHasMadeIntroduction = hasMadeIntroduction !== this.props.hasMadeIntroduction;

    if (isChangingCurrentEvent || isChangingHasCuratedTickets || isChangingHasMadeIntroduction) {
      this.taraMessage = this.getTaraMessage(nextProps);
    }
  }

  componentWillUnmount() {
    this.hasMounted = false;
  }

  taraMessage = this.getTaraMessage(this.props);

  getTaraMessage(props) {
    const {
      currentEvent,
      hasCuratedTickets,
      hasMadeIntroduction,
    } = props;

    if (!currentEvent) {
      return <DefaultMessage />;
    }

    const eventName = currentEvent.name;

    if (!hasCuratedTickets && hasMadeIntroduction) {
      return (
        <CuratedTaraMessage eventName={eventName} />
      );
    }

    if(!hasMadeIntroduction)
      this.props.analyticsRecordActionRequest('tara','introduction', eventName);

    return hasMadeIntroduction ?
      (<TaraMessage />)
      :
      (<IntroductionTaraMessage eventName={eventName} />);
  }

  setPrices = (priceLow, priceHigh) => this.setState({
    priceLow,
    priceHigh,
  });

  setQuantity = quantity => this.setState({ quantity });

  setIsReady = (isReady) => {
    if (this.hasMounted) {
      this.setState({ isReady });
    }
  };

  setPersona = (persona) => {
    if (persona === this.state.persona) {
      this.setState({ persona: null });
    } else {
      this.setState({ persona });
    }
  };

  setPrice = (type, value) => this.setState({ [type]: value });

  setPopoverScrollRef = ref => this.popoverScrollRef = ref;

  createHandleClickPrice = (type, value) => (e) => {
    this.setPrice(type, value);
  };

  handleOk = (e) => {
    if (this.state.persona && this.state.quantity) {
      this.props.onClickFindMyTickets(e, this.formState);
    }
  };

  handleClickBrowseAllTickets = (e) => {
    this.props.onClickBrowseAllTickets(e, this.formState);
    this.props.analyticsRecordActionRequest('tara','browse', null);
  };

  handleClickFindMyTickets = (e) => {
    this.props.onClickFindMyTickets(e, this.formState);
    this.props.analyticsRecordActionRequest('tara','find', null);
  };

  handleClickPersona = (persona) => {
    this.setPersona(persona);
    this.props.analyticsRecordActionRequest('tara','set_persona', persona);
  };

  handleClickQuantity = (quantity) => {
    this.setQuantity(quantity);
    this.props.analyticsRecordActionRequest('tara','set_quanity', quantity);
  };

  handleChangePrice = (values) => {
    const priceLow = values[0];
    const priceHigh = values[1];

    this.setPrices(priceLow, priceHigh);
    this.props.analyticsRecordActionRequest('tara','set_price', [priceLow, priceHigh]);
  };

  renderPopoverOpen = (this.props.hasButton ? handleOpen => (
    <Button onClick={handleOpen}>
      Tara
    </Button>
  ) : null);

  renderFooter() {
    if (!this.state.persona || !this.state.quantity) {
      return null;
    }

    if (!this.renderedFooterOnce && this.popoverScrollRef) {
      this.renderedFooterOnce = true;

      setTimeout(() => {
        const { clientHeight, scrollTop, scrollHeight } = this.popoverScrollRef;
        const shouldBeAt = scrollHeight - clientHeight;

        if (scrollTop === 0) {
          this.popoverScrollRef.scrollTop = shouldBeAt;
        }
      }, 20);
    }

    return (
      <TaraPopoverFooter
        className={this.props.isAnimated ? 'animated fadeIn' : undefined}
      >
        <Button
          color="accent"
          label="Find My Tickets"
          isRaised
          onClick={this.handleClickFindMyTickets}
          style={styleButton}
        />

        {this.props.canDismiss && (
          <Button
            className="LeftButton"
            color="primary"
            label="Browse All Tickets"
            onClick={this.handleClickBrowseAllTickets}
            style={styleButton}
          />
        )}
      </TaraPopoverFooter>
    );
  }

  render() {
    const {
      isReady,
      quantity,
      persona,
      priceLow,
      priceHigh,
    } = this.state;

    const { isAnimated } = this.props;

    return (
      <Popover
        onOk={this.handleOk}
        onCancel={this.props.onCancel}
        renderOpen={this.renderPopoverOpen}
        isOpen={this.props.isOpen}
        hasOk={false}
        popoverScrollRef={this.setPopoverScrollRef}
        popoverWidth={480}
        closeOnClickBackdrop={this.props.canDismiss}
      >
        <TaraHeader
          message={this.taraMessage}
          style={{
            marginBottom: 32,
          }}
        />

        {isReady && (
          <TaraQuestionPersona
            persona={persona}
            isAnimated={isAnimated}
            onClick={this.handleClickPersona}
          />
        )}

        {!!persona && (
          <TaraQuestionQuantity
            quantity={quantity}
            persona={persona}
            isAnimated={isAnimated}
            onClick={this.handleClickQuantity}
          />
        )}

        {!!persona && !!quantity && (
          <TaraQuestionPrice
            quantity={quantity}
            persona={persona}
            priceLow={priceLow}
            priceHigh={priceHigh}
            onChange={this.handleChangePrice}
            isAnimated={isAnimated}
          />
        )}

        {this.renderFooter()}
      </Popover>
    );
  }
}

export default TaraPopover;



// WEBPACK FOOTER //
// ./src/components/features/Tara/TaraPopover.js